import { LabCalendar } from '@/src/components/labdashboard/LabCalendar';
import { UtilizationGauge } from '@/src/components/labdashboard/UtilizationGauge';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Box, Button, Grid, KeyValuePairs, Link, SpaceBetween, Table } from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EventListFilter, NullableNumber } from '@/src/types/common';
import { Event, EventStatus } from '../../types/Event';
import { EVENT_DETAILS_ROUTES } from '@/src/routes';
import { LabDashboardAPI } from '@/src/api/LabDashboardAPI';
import { EventsAPI } from '@/src/api/EventsAPI';
import { DailySummaryMap, LabDashboardEventSummary } from '@/src/types/LabDashboard';
import JamSpinner from '@/src/components/common/JamSpinner/JamSpinner';
import styles from './LabMetrics.module.scss';
import {
  getCachedLabDashboard,
  getLatestCachedLabDashboard,
  LabDashboardCacheEntry,
  setCachedLabDashboard,
} from './labDashboardCache';

interface LabMetricsProps {
  labDashboardAPI: LabDashboardAPI;
  eventsApi: EventsAPI;
}

const LabMetrics: React.FC<LabMetricsProps> = ({ labDashboardAPI, eventsApi }) => {
  const { t } = useTranslation();
  const [usagePercent, setUsagePercent] = useState<NullableNumber>();
  const [activeLabCount, setActiveLabCount] = useState<NullableNumber>();
  const [availableLabs, setAvailableLabs] = useState<number>();
  const [calendarEvents, setCalendarEvents] = useState<LabDashboardEventSummary[]>([]);
  const [dailySummary, setDailySummary] = useState<DailySummaryMap>({});
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const UPCOMING_EVENT_LIMIT = 7;

  /**
   * Fetches upcoming events with pagination, filtering to approved non-test events.
   */
  const fetchUpcomingEvents = async (limit: number): Promise<Event[]> => {
    const currentDay = new Date().toLocaleDateString('en-CA');
    const filter: EventListFilter = {
      limit: 30,
      status: EventStatus.NOT_STARTED,
      start: currentDay,
      end: null,
      includeFields: [
        'title',
        'name',
        'startDate',
        'maxTeamSize',
        'maxExpectedParticipants',
        'approvalStatus',
        'type',
        'oneClickTestEvent',
        'challengeDescriptors',
      ],
    };

    const upcomingEvents: Event[] = [];
    let page = 1;
    let hasMore = true;

    while (upcomingEvents.length < limit && hasMore) {
      const response = await eventsApi.getEvents({ ...filter, page });

      for (const evt of response.events) {
        if (upcomingEvents.length === limit) break;
        if (evt.wasEverApproved && !evt.isOneClickTestEvent) {
          upcomingEvents.push(evt);
        }
      }

      const totalFetched = page * (filter.limit ?? 30);
      hasMore = totalFetched < response.count;
      page++;
    }

    return upcomingEvents;
  };

  // Utilization is live state returned with every month, so always show the newest cached copy.
  const applyUtilization = useCallback((entry: LabDashboardCacheEntry | undefined) => {
    if (!entry) {
      return;
    }
    const { activeLabUsePercentage: pct, activeLabCount: count } = entry.response;
    setUsagePercent(pct);
    setActiveLabCount(count);
    setAvailableLabs(pct && pct > 0 && count ? Math.round(count / (pct / 100)) - count : undefined);
  }, []);

  const applyDashboard = useCallback(
    (entry: LabDashboardCacheEntry) => {
      setCalendarEvents(entry.response.events);
      setDailySummary(entry.response.dailySummary);
      applyUtilization(getLatestCachedLabDashboard() ?? entry);
    },
    [applyUtilization]
  );

  /**
   * Loads the dashboard for `month`, serving it from the per-month cache when fresh.
   * Upcoming events don't depend on the month, so they're only fetched when requested (mount / refresh).
   */
  const loadContent = useCallback(
    async (month: Date, { forceRefresh = false, includeUpcomingEvents = false } = {}) => {
      const monthParam = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
      const cached = forceRefresh ? undefined : getCachedLabDashboard(monthParam);

      if (cached && !includeUpcomingEvents) {
        applyDashboard(cached);
        return;
      }

      setLoading(true);
      try {
        const dashboardPromise = cached
          ? Promise.resolve(cached)
          : labDashboardAPI
              .fetchLabDashboard(monthParam)
              .then((response) => setCachedLabDashboard(monthParam, response));
        const eventsPromise = includeUpcomingEvents ? fetchUpcomingEvents(UPCOMING_EVENT_LIMIT) : undefined;

        const [dashboardResult, eventsResult] = await Promise.allSettled([
          dashboardPromise,
          eventsPromise ?? Promise.resolve([]),
        ]);

        if (eventsPromise) {
          if (eventsResult.status === 'fulfilled') {
            setEvents(eventsResult.value);
          } else {
            // eslint-disable-next-line no-console
            console.error('error fetching upcoming events', eventsResult.reason);
            setEvents([]);
          }
        }

        if (dashboardResult.status === 'fulfilled') {
          applyDashboard(dashboardResult.value);
        } else {
          // eslint-disable-next-line no-console
          console.error('error fetching lab dashboard', dashboardResult.reason);
          // Refresh clears the gauge up front; refill it from other months rather than leave it blank.
          applyUtilization(getLatestCachedLabDashboard());
        }
      } finally {
        setLoading(false);
      }
    },
    [labDashboardAPI, eventsApi, applyDashboard, applyUtilization]
  );

  const handleMonthChange = (newMonth: Date) => {
    setSelectedMonth(newMonth);
    loadContent(newMonth);
  };

  // Bypasses the cache for the selected month only; other cached months are left intact.
  const handleUtilizationRefresh = () => {
    setEvents([]);
    setActiveLabCount(undefined);
    setAvailableLabs(undefined);
    setUsagePercent(undefined);
    loadContent(selectedMonth, { forceRefresh: true, includeUpcomingEvents: true });
  };

  useEffect(() => {
    loadContent(selectedMonth, { includeUpcomingEvents: true });
  }, [loadContent]);

  const keyValueItems = useMemo(
    () => [
      { label: t(i18nKeys.labDashboard.utilizationSummary.activeLabs), value: activeLabCount },
      {
        label: t(i18nKeys.labDashboard.utilizationSummary.availableLabs),
        value: availableLabs,
      },
    ],
    [activeLabCount, availableLabs, t]
  );

  const tableColumnDefinitions = useMemo(
    () => [
      {
        id: 'eventTitle',
        header: t(i18nKeys.labDashboard.upcomingEvents.eventTitle),
        cell: (item: Event) => (
          <Link target="_blank" href={EVENT_DETAILS_ROUTES.Summary.resolve(item.name)}>
            {item.title}
          </Link>
        ),
        minWidth: 150,
      },
      {
        id: 'estimatedLabs',
        header: t(i18nKeys.labDashboard.upcomingEvents.estimatedLabs),
        cell: (item: Event) => (
          <div className={styles.estimatedLabsCell}>
            {item.maxExpectedParticipants
              ? Math.ceil(item.maxExpectedParticipants / item.maxTeamSize) * item.numChallenges
              : '-'}
          </div>
        ),
        minWidth: 100,
      },
    ],
    [t]
  );

  return loading ? (
    <JamSpinner />
  ) : (
    <Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
      <div className={styles.calendarPanel}>
        <LabCalendar
          dailySummary={dailySummary}
          calendarEvents={calendarEvents}
          onMonthChange={handleMonthChange}
          initialMonth={selectedMonth}
        />
      </div>
      <SpaceBetween direction="vertical" size="xs">
        <SpaceBetween size="s">
          <div className={styles.utilizationHeader}>
            <Box variant="h1">{t(i18nKeys.labDashboard.utilizationSummary.title)}</Box>
            <Button
              variant="inline-icon"
              iconName="refresh"
              ariaLabel={t(i18nKeys.labDashboard.utilizationSummary.refresh)}
              onClick={handleUtilizationRefresh}
            />
          </div>
          <UtilizationGauge value={usagePercent} />
          <KeyValuePairs items={keyValueItems} columns={2} minColumnWidth={50} />
        </SpaceBetween>
        <SpaceBetween size="s">
          <Box variant="h1">{t(i18nKeys.labDashboard.upcomingEvents.title)}</Box>
          <Table columnDefinitions={tableColumnDefinitions} items={events} wrapLines variant="borderless" />
        </SpaceBetween>
      </SpaceBetween>
    </Grid>
  );
};

export default LabMetrics;
